/**
 * @description :: Edits the Carousel Banner and static images within the website landing page
 */
import React, { useState, useEffect, useRef } from "react";
import {
  Modal,
  TextInput,
  Button,
  Group,
  ColorInput,
  Text,
  Select,
  Stack,
  ActionIcon,
  Checkbox,
} from "@mantine/core";
import classes from "./edit.module.css";
import { RowDataBannerWeb } from "./types";
import { Dropzone } from "@mantine/dropzone";
import { IconPhoto, IconX, IconPlus, } from "@tabler/icons-react";

interface EditBannerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onEditBanner: (editedBannerData: RowDataBannerWeb) => void;
  bannerData: RowDataBannerWeb;
}

const EditBannerModal: React.FC<EditBannerModalProps> = ({
  isOpen,
  onClose,
  onEditBanner,
  bannerData,
}) => {
  const [editedBanner, setEditedBanner] = useState<RowDataBannerWeb>(bannerData);

  const [descs, setDescs] = useState<string[]>([])
  const [descInput, setDescInput] = useState<string>('')

  const imageDropzoneRef = useRef<() => void>(null);
  const imageMobileDropzoneRef = useRef<() => void>(null);

  useEffect(()=>{
    setEditedBanner({ ...bannerData, descs: JSON.stringify(descs) })
  },[descs.length])

  useEffect(() => {
    if (isOpen) {
      setEditedBanner(bannerData);
      setDescs(bannerData?.descs ? JSON.parse(bannerData?.descs) : [])
    }
  }, [bannerData, isOpen]);

  const handleImageUpload = (files: File[]) => {
    const file = files[0];
    if (file) {
      setEditedBanner({ ...editedBanner, img: file });
    }
  };

  const handleImageMobileUpload = (files: File[]) => {
    const file = files[0];
    if (file) {
      setEditedBanner({ ...editedBanner, imgMobile: file });
    }
  };

  const handleEdit = () => {
    // Perform validation or additional logic as needed
    onEditBanner(editedBanner);
    onClose();
  };

  const addDesc = () => {
    if(descInput.trim().length < 1){
        alert("Please enter description");
        return;
    }
    descs.push(descInput.trim())
    setDescInput('')
}

const removeDesc = (index: number) => {
    setDescs(item=> item.filter((s,i)=>(i != index)))
}

  return (
    <Modal
      title={
        <div style={{ textAlign: "center", fontWeight: "bold" }}>
          Edit Banner
        </div>
      }
      opened={isOpen}
      onClose={onClose}
    >
    
    <Text className={classes.Text}>
        Image <span className={classes.format}>(.jpg, .png)</span>
      </Text>
      <Dropzone
        onDrop={handleImageUpload}
        accept={["image/*"]}
        openRef={imageDropzoneRef}
      >
        <div className={classes.imageContainer}>
          {editedBanner.img ? (
            <img
              src={
                !editedBanner.img.path
                  ? editedBanner.img
                  : URL.createObjectURL(editedBanner.img)
              }
              alt="Image"
              className={classes.imageSize}
            />
          ) : (
            <p>
              <IconPhoto />
            </p>
          )}
        </div>
      </Dropzone>
      <Group>
        <Button
          onClick={() => imageDropzoneRef.current?.()}
          className={classes.imageButton}
        >
          <IconPhoto />
          Upload Image
        </Button>
      </Group>

    <Text className={classes.Text}>
        Image Mobile <span className={classes.format}>(.jpg, .png)</span>
      </Text>
      <Dropzone
        onDrop={handleImageMobileUpload}
        accept={["image/*"]}
        openRef={imageMobileDropzoneRef}
      >
        <div className={classes.imageContainer}>
          {editedBanner.imgMobile ? (
            <img
              src={!editedBanner.imgMobile.path
                ? editedBanner.imgMobile
                : URL.createObjectURL(editedBanner.imgMobile)}
              alt="Image"
              className={classes.imageSize}
            />
          ) : (
            <p>
              <IconPhoto />
            </p>
          )}
        </div>
      </Dropzone>
      <Group>
        <Button
          onClick={() => imageMobileDropzoneRef.current?.()}
          className={classes.imageButton}
        >
          <IconPhoto />
          Upload Image
        </Button>
      </Group>

      <TextInput
        label="Title"
        maxLength={255}
        mb="sm"
        value={editedBanner.title}
        onChange={(event) =>
          setEditedBanner({ ...editedBanner, title: event.currentTarget.value })
        }
      />

    <Stack gap={4}>
        {descs.length > 0 ? <Text className={classes.Text}>Descriptions</Text> : null}
        {descs.map((item, i)=>(
            <div key={i} style={{display: 'flex', gap: 10, width: '100%'}}>
                <div style={{flex: 1}}>
                <TextInput
                    // disabled={createvenueMutation.isLoading}
                    value={item}
                    readOnly
                />
                </div>
                <ActionIcon onClick={() => removeDesc(i)} size='lg' variant="outline" color='red'>
                    <IconX size={20} />
                </ActionIcon>
            </div>
        ))
        }
        </Stack>

        <Text className={classes.Text}>Add Description</Text>
        <div style={{display: 'flex', gap: 10, width: '100%'}}>
            <div style={{flex: 1}}>
                <TextInput
                // label="Terms"
                placeholder="Input desc"
                mb="sm"
                maxLength={255}
                value={descInput}
                onChange={(event) =>
                    setDescInput(event.currentTarget.value)
                }
                />
            </div>
            <ActionIcon onClick={() => addDesc()} size='lg' variant="outline" color='green'>
                <IconPlus size={20} />
            </ActionIcon>
        </div>

      {/* <ColorInput
        label="Color"
        placeholder="color"
        mb="sm"
        value={editedBanner.color}
        onChange={(color) => setEditedBanner({ ...editedBanner, color: color })}
      /> */}

        <TextInput
        label="Button Title"
        maxLength={255}
        mb="sm"
        value={editedBanner.buttonTitle}
        onChange={(event) =>
          setEditedBanner({ ...editedBanner, buttonTitle: event.currentTarget.value })
        }
      />

      <Select
        label="Banner Type"
        placeholder="Select banner type"
        data={[
          { value: 'carousel', label: 'Carousel' },
          { value: 'prebuiltPC', label: 'Prebuilt PC' },
          { value: 'pcBuilder', label: 'PC Builder' },
          { value: 'customPC', label: 'Custom PC' },
        ]}
        value={editedBanner.type}
        onChange={(value) => {
          if (value) {
            if (value == 'prebuiltPC'){
              setEditedBanner({ ...editedBanner, type: value, route: 'prebuild/all'});
            } else if (value == 'pcBuilder') {
              setEditedBanner({ ...editedBanner, type: value, route: 'builder/quick'});
            } else if (value == 'customPC'){
              setEditedBanner({ ...editedBanner, type: value, route: 'builder/custom'});
            } else {
              setEditedBanner({ ...editedBanner, type: value, route: ""});
            }
          }
        }}
        mb="sm"
      />

        <TextInput
        label="Route"
        placeholder="https://example.com"
        maxLength={255}
        mb="sm"
        value={editedBanner.route}
        disabled={editedBanner.type !== 'carousel'} 
        onChange={(event) =>
          setEditedBanner({ ...editedBanner, route: event.currentTarget.value })
        }
      />

<Checkbox
      checked={editedBanner.newTab}
      onChange={(event) => setEditedBanner({...editedBanner, newTab: event.currentTarget.checked})}
      label="New tab?"
      mb="sm"
    />

    <Checkbox
      checked={editedBanner.darkMode}
      onChange={(event) => setEditedBanner({...editedBanner, darkMode: event.currentTarget.checked})}
      label="Dark mode?"
      mb="sm"
    />

      <Group className={classes.buttonBottom}>
        <Button onClick={() => onClose()} className={classes.buttonCancel}>
          Back
        </Button>
        <Button onClick={handleEdit} className={classes.buttonSaveChanges}>
          Save Changes
        </Button>
      </Group>
    </Modal>
  );
};

export default EditBannerModal;
